<template>
  <div>
    <template v-if="existingContact">
      <contact-already-exist-block :contact="existingContact" />

      <hr class="mt-6 mb-6" />
    </template>

    <v-alert
      v-if="errorMessages.length > 0"
      type="error"
      dismissible
      @input="clearErrors"
      class="mb-4"
    >
      <div v-for="(error, index) in errorMessages" :key="index">
        {{ error }}
      </div>
    </v-alert>

    <contact-form-fields
      v-if="contactForm"
      ref="contactFormContainer"
      :contact="contactForm"
      :editableFields="editableFields"
    />

    <portal to="createContactAction">
      <v-btn
        :color="modal ? 'appMain' : 'primary'"
        :loading="contactLoading || updating"
        :disabled="disabled"
        @click="saveContact"
      >
        Create
      </v-btn>
    </portal>

    <div class="spinner-overlay" v-if="contactLoading || updating">
      <spinner />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import clone from 'lodash/clone';

import ContactFormFields from '../components/ContactFormFields';
import ContactAlreadyExistBlock from '@/views/contacts/components/ContactAlreadyExistBlock.vue';

export default {
  name: 'ContactForm',
  components: {
    ContactFormFields,
    ContactAlreadyExistBlock,
  },
  props: {
    returnContact: Boolean,
    modal: Boolean,
    search: String,
    disabled: Boolean,
    editableFields: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      contactForm: null,
      existingContact: null,
      updating: false,
      errorMessages: [],
    };
  },
  computed: {
    ...mapGetters(['contactLoading']),
    ...mapGetters('fields', {
      fields: 'all',
      fieldsLoaded: 'loaded',
      getSingleField: 'getSingleField',
    }),
  },
  watch: {
    fieldsLoaded(newVal) {
      if (newVal) {
        this.setContactForm();
      }
    },
    contactForm: {
      deep: true,
      handler() {
        if (this.existingContact) {
          this.existingContact = null;
        }
        if (this.errorMessages.length > 0) {
          this.clearErrors();
        }
      },
    },
  },
  methods: {
    ...mapActions(['createContact', 'loadSingleContactShort']),
    ...mapActions('fields', { loadFields: 'load' }),
    isFormValid() {
      return this.$refs.contactFormContainer.validate();
    },

    clearErrors() {
      this.errorMessages = [];
    },

    isFieldEditable({ name, form_hidden }) {
      if (form_hidden) return false;
      if (this.editableFields.length === 0) return true;
      return this.editableFields.includes(name);
    },

    getContactTemplate() {
      const fieldsObj = this.fields.find((i) => i.type === 'default')['fields'];
      const fieldsArr = Object.values(fieldsObj)
        .sort((a, b) => a.order - b.order)
        .filter((i) => this.isFieldEditable(i));

      return fieldsArr.reduce((acc, i) => {
        acc[i.name] = i.type === 'list' ? [] : null;
        return acc;
      }, {});
    },

    setContactForm() {
      if (this.fieldsLoaded) {
        this.contactForm = clone(this.getContactTemplate());
      }

      if (this.search && this.contactForm) {
        let [firstName, lastName] = this.search.trim().split(' ');
        this.contactForm.first_name = firstName || '';
        this.contactForm.last_name = lastName || '';
      }
    },

    clearData() {
      for (let key in this.contactForm) {
        this.contactForm[key] = Array.isArray(this.contactForm[key]) ? [] : '';
      }

      this.$refs.contactFormContainer.reset();
    },

    getFormData() {
      let formData = Object.fromEntries(
        Object.entries(this.contactForm).filter(([_, value]) => {
          return value || value === false;
        })
      );

      for (let key in formData) {
        const field = this.getSingleField(key);
        if (field.type === 'related_custom_object') {
          formData[`${key}_id`] = formData[key];
          delete formData[key];
        }
      }

      return formData;
    },

    async saveContact() {
      if (!this.isFormValid()) {
        return;
      }

      this.clearErrors();
      this.updating = true;

      const isContactExists = await this.isContactAlreadyExists();
      if (isContactExists) {
        this.updating = false;
        return false;
      }

      try {
        const contact = await this.createContact(this.getFormData());
        this.$emit('created', contact);
      } catch (e) {
        const errorData = e?.data?.data;

        if (errorData?.[0]?.messages) {
          Object.values(errorData[0].messages).forEach((msgs) => {
            this.errorMessages.push(...msgs);
          });
        } else {
          this.errorMessages.push(
            e?.response?.data?.message || 'Failed to create contact'
          );
        }
      } finally {
        this.updating = false;
      }
    },

    async isContactAlreadyExists() {
      this.existingContact = await this.findExistingContact();
      return !!this.existingContact;
    },

    async findExistingContact() {
      const searchableFields = ['mobile', 'email'];

      if (searchableFields.every((f) => !this.contactForm[f])) {
        return null;
      }

      let filters = { $or: [] };
      searchableFields.forEach((f) => {
        if (this.contactForm[f]) {
          const { name } = this.getSingleField(f) || {};
          filters.$or.push({
            [name]: {
              $in: this.contactForm[f],
            },
          });
        }
      });

      return await this.loadSingleContactShort({ filters });
    },
  },
  mounted() {
    this.loadFields({ checkLoaded: true });
    this.setContactForm();
  },
};
</script>
